// Verdict & score helpers: friendly, plain-English
export const VERDICT_META = {
  verified: {
    label: "Verified",
    short: "Likely accurate",
    color: "#7dd3a0",
    bg: "rgba(125, 211, 160, 0.1)",
    border: "rgba(125, 211, 160, 0.3)",
    desc: "The main claims align with credible sources and pass internal coherence checks.",
  },
  mostly_true: {
    label: "Mostly True",
    short: "Largely accurate",
    color: "#a8e0b8",
    bg: "rgba(168, 224, 184, 0.08)",
    border: "rgba(168, 224, 184, 0.25)",
    desc: "The core is accurate, but minor caveats or context were noted.",
  },
  mixed: {
    label: "Mixed",
    short: "Partly true, partly not",
    color: "#f5b041",
    bg: "rgba(245, 176, 65, 0.08)",
    border: "rgba(245, 176, 65, 0.3)",
    desc: "Contains a blend of accurate and questionable elements, read carefully.",
  },
  misleading: {
    label: "Misleading",
    short: "True facts, wrong picture",
    color: "#f5a04a",
    bg: "rgba(245, 160, 74, 0.08)",
    border: "rgba(245, 160, 74, 0.3)",
    desc: "Selective framing or missing context paints a distorted impression.",
  },
  false: {
    label: "False",
    short: "Likely untrue",
    color: "#f08a8a",
    bg: "rgba(240, 138, 138, 0.08)",
    border: "rgba(240, 138, 138, 0.3)",
    desc: "Credible evidence directly contradicts the central claims.",
  },
  unverifiable: {
    label: "Unverifiable",
    short: "Not enough evidence",
    color: "#a8a8b0",
    bg: "rgba(168, 168, 176, 0.06)",
    border: "rgba(168, 168, 176, 0.25)",
    desc: "There isn't enough public evidence to make a confident judgement.",
  },
};

export const SCORE_DIMENSIONS = [
  {
    key: "truth",
    label: "Truthfulness",
    short: "How factually accurate the content appears.",
    helper: "Higher means the claims are more likely to be true.",
    scale: (v) => v >= 75 ? "Very accurate" : v >= 50 ? "Mostly accurate" : v >= 25 ? "Some inaccuracies" : "Likely inaccurate",
  },
  {
    key: "bias",
    label: "Neutrality",
    short: "How balanced and unbiased the framing is.",
    helper: "Higher means more neutral. Lower means more one-sided.",
    scale: (v) => v >= 75 ? "Very neutral" : v >= 50 ? "Mostly neutral" : v >= 25 ? "Noticeably biased" : "Heavily biased",
  },
  {
    key: "source_credibility",
    label: "Source Quality",
    short: "How trustworthy the sources cited are.",
    helper: "Based on the reputation of the sources referenced inside the content.",
    scale: (v) => v >= 75 ? "Strong sources" : v >= 50 ? "Decent sources" : v >= 25 ? "Weak sources" : "Poor sources",
  },
  {
    key: "sentiment",
    label: "Emotional Tone",
    short: "The emotional pitch of the writing.",
    helper: "50 is neutral. Below is negative, above is positive.",
    scale: (v) => v >= 75 ? "Positive tone" : v >= 55 ? "Slightly positive" : v >= 45 ? "Neutral tone" : v >= 25 ? "Slightly negative" : "Negative tone",
  },
  {
    key: "objectivity",
    label: "Objectivity",
    short: "Fact vs. opinion balance.",
    helper: "Higher means more facts, lower means more personal opinion.",
    scale: (v) => v >= 75 ? "Very factual" : v >= 50 ? "Mostly factual" : v >= 25 ? "Mostly opinion" : "Heavily opinionated",
  },
  {
    key: "source_authority",
    label: "Author Authority",
    short: "Domain expertise of the author or publisher.",
    helper: "Reflects how qualified the source is on this topic.",
    scale: (v) => v >= 75 ? "Recognised authority" : v >= 50 ? "Credible voice" : v >= 25 ? "Limited authority" : "Unverified author",
  },
  {
    key: "cross_platform_consistency",
    label: "Corroboration",
    short: "Does other reporting agree?",
    helper: "Higher means many credible outlets report the same facts.",
    scale: (v) => v >= 75 ? "Widely corroborated" : v >= 50 ? "Some corroboration" : v >= 25 ? "Little corroboration" : "Not corroborated",
  },
  {
    key: "semantic_consistency",
    label: "Internal Logic",
    short: "Does the content contradict itself?",
    helper: "Higher means the argument flows without internal contradictions.",
    scale: (v) => v >= 75 ? "Logically sound" : v >= 50 ? "Mostly coherent" : v >= 25 ? "Some contradictions" : "Contradicts itself",
  },
];

export const scoreColor = (v) => {
  if (v >= 75) return "#7dd3a0"; // soft green
  if (v >= 50) return "#f5b041"; // honey amber
  if (v >= 25) return "#f5a04a"; // soft orange
  return "#f08a8a"; // soft coral
};

export const credBadge = (cred) => {
  if (typeof cred === "number") {
    if (cred >= 75) return { label: "High credibility", color: "#7dd3a0" };
    if (cred >= 50) return { label: "Medium credibility", color: "#f5b041" };
    if (cred >= 25) return { label: "Low credibility", color: "#f08a8a" };
    return { label: "Unverified", color: "#a8a8b0" };
  }
  const c = String(cred || "").toLowerCase();
  if (c === "high") return { label: "High credibility", color: "#7dd3a0" };
  if (c === "medium") return { label: "Medium credibility", color: "#f5b041" };
  if (c === "low") return { label: "Low credibility", color: "#f08a8a" };
  return { label: "Unverified", color: "#a8a8b0" };
};

export const formatRelative = (iso) => {
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch { return ""; }
};

export const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export const downloadJSON = (obj, filename) => {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

export const prettify = (s) => {
  if (!s) return "-";
  return String(s).replace(/_/g, " ").replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};
